import sha1 from 'sha1'
import { getJsapiTicket } from "@/api/common"
import wx from 'weixin-js-sdk';
import * as dd from 'dingtalk-jsapi'; // 引入钉钉SDK

// ================= 环境判断 =================
const ua = navigator.userAgent.toLowerCase();
export const isDingTalk = () => ua.indexOf('dingtalk') > -1;
export const isWeCom = () => ua.indexOf('wxwork') > -1;
export const isWeChat = () => ua.indexOf('micromessenger') > -1 && !isWeCom();

// ================= 配置项 (建议从环境变量获取) =================
// 微信公众号 AppID
const WX_APPID = window.context.VITE_APPID; 
// 企业微信 CorpID (企业微信后台获取)
const WECOM_CORPID = window.context.VITE_WECOM_CORPID || window.context.VITE_APPID; 
// 钉钉 CorpID (钉钉开发者后台获取)
const DD_CORPID = window.context.VITE_DD_CORPID; 

export let wxSdkIsReady = false // 微信/企微 SDK 是否已初始化

/**
 * 触发授权跳转 (仅适用于 微信公众号 和 企业微信)
 * 钉钉不需要跳转，使用的是静默获取
 * @param {string} scope 
 */
export function getAuth(scope = 'snsapi_userinfo') {
  console.log("location.href", location.href);
  const redirectUri = encodeURIComponent(location.href);
  
  // 基础 URL
  let baseUrl = `https://open.weixin.qq.com/connect/oauth2/authorize`;
  
  if (isWeCom()) {
    console.log("企业微信逻辑");
    
    // ?appid=CORPID&redirect_uri=REDIRECT_URI&response_type=code&scope=snsapi_base&state=STATE&agentid=AGENTID#wechat_redirect
    // === 企业微信逻辑 ===
    // 企业微信使用 CorpID 作为 appid 参数
    // scope 通常为 snsapi_base 或 snsapi_privateinfo
    const wecomScope = 'snsapi_privateinfo'; 
    // agentid 是可选的，但建议加上，防止多应用冲突
    const agentIdPart = window.context.VITE_AGENT_ID ? `&agentid=${window.context.VITE_AGENT_ID}` : '';
    
    window.location.href = `${baseUrl}?appid=${WECOM_CORPID}&redirect_uri=${redirectUri}&response_type=code&scope=${wecomScope}${agentIdPart}&state=STATE#wechat_redirect`;
  } else if (isWeChat()) {
    console.log("微信公众号逻辑");
    // === 微信公众号逻辑 ===
    window.location.href = `${baseUrl}?appid=${WX_APPID}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&state=STATE#wechat_redirect`;
  } else if (isDingTalk()) {
    // === 钉钉逻辑 ===
    // 钉钉通常不需要通过 URL 跳转获取 Code，除非你做的是扫码登录第三方网站。
    // 在钉钉应用内，直接调用 JSAPI 获取，不需要 location.href 跳转。
    console.warn("钉钉环境通常不需要 URL 重定向授权，请直接调用 getDingTalkCode()");
  }
}

/**
 * 获取 URL 上的 Code (适用于 微信公众号 和 企业微信)
 */
export function getCode() {
  // 如果是钉钉，URL 上通常没有 code，返回 null，交由 init.js 单独处理
  if (isDingTalk()) return null;

  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('code')) {
    const codes = urlParams.getAll('code');
    const code = codes[codes.length - 1]; // 取最后一个

    // ✅ 关键：清除 URL 中的 code 和 state 参数（可选）
    const cleanUrl = window.location.origin + window.location.pathname + window.location.hash;
    window.history.replaceState(null, '', cleanUrl); // 不会刷新页面，但清除 query

    return code;
  } else {
    getAuth(); // 如果没有 code，触发跳转
    return null;
  }
}

/**
 * 获取钉钉 Code (异步)
 * 钉钉的机制不同，必须异步调用 JSAPI
 */
export function getDingTalkCode() {
  return new Promise((resolve, reject) => {
    if (!isDingTalk()) {
      reject('非钉钉环境');
      return;
    }
    
    dd.ready(() => {
      dd.runtime.permission.requestAuthCode({
        corpId: DD_CORPID, // 必填，企业ID
        onSuccess: function (result) {
          // result.code 即为免登码
          resolve(result.code);
        },
        onFail: function (err) {
          console.error('钉钉获取免登码失败', err);
          reject(err);
        }
      });
    });
  });
}

// ================= SDK 初始化 =================

// 微信/企业微信 JSSDK 初始化
export function wxJsSDKInit() {
  return new Promise(async (res, rej) => {
    // 如果是钉钉，不需要初始化微信 SDK
    if (isDingTalk()) {
      res(); 
      return;
    }

    if (wxSdkIsReady) {
      res()
      return
    }

    try {
      const signatureRes = await getSignature()
      
      // 企业微信获取的 ticket 结构可能略有不同，需根据后端返回调整
      // 通常企业微信也需要 config，但增加了 beta: true 字段以支持新接口
      let configData = {
        appId: signatureRes.jsapi_ticket.body.app_id || signatureRes.appId, 
        timestamp: signatureRes.jsapi_ticket.body.time_millis || signatureRes.timestamp,
        nonceStr: signatureRes.jsapi_ticket.body.noncestr || signatureRes.nonceStr,
        signature: signatureRes.jsapi_ticket.body.signature || signatureRes.signature,
        jsApiList: ['getLocation', 'scanQRCode', 'invoke', 'openLocation'],
      }

      wx.config({
        // debug: true,
        beta: true, // 必须这么写，否则在企业微信中调用部分接口会有问题
        ...configData
      })

      wx.ready(function () {
        wxSdkIsReady = true
        res()
      })

      wx.error(function (e) {
        console.error('wx.config error', e);
        wxSdkIsReady = false
        rej(e)
      })
    } catch (error) {
      rej(error)
    }
  })
}

// ================= 功能函数适配 =================

// 统一扫码功能
export function scanQRCode() {
  return new Promise((resolve, reject) => {
    if (isDingTalk()) {
      // 钉钉扫码
      dd.biz.util.scan({
        type: 'all', // type 为 all、qrCode、barCode
        onSuccess: function(data) {
          resolve({ resultStr: data.text }); // 保持返回结构统一
        },
        onFail: function(err) {
          reject({ message: err.errorMessage, type: 1 });
        }
      });
    } else {
      // 微信/企微扫码
      wx.scanQRCode({
        needResult: 1, // 默认为0，扫描结果由微信处理，1则直接返回扫描结果
        scanType: ["qrCode", "barCode"],
        success: function (res) {
          resolve(res);
        },
        fail: function (e) {
          reject({ message: e.errMsg, type: 1 });
        },
        cancel: function (res) {
          reject({ message: '用户拒绝授权获取摄像头', type: 2 });
        }
      });
    }
  })
}

// 统一获取地理位置
export function getLocation() {
  return new Promise((resolve, reject) => {
    if (isDingTalk()) {
      // 钉钉定位
      dd.device.geolocation.get({
        targetAccuracy: 200,
        coordinate: 1,
        withReGeocode: false,
        useCache:true,
        onSuccess : function(result) {
            resolve({
              latitude: result.latitude,
              longitude: result.longitude,
              accuracy: result.accuracy
            });
        },
        onFail : function(err) {
            reject({ message: err.errorMessage, type: 1 });
        }
      });
    } else {
      // 微信/企微定位
      wx.getLocation({
        type: 'wgs84', 
        success: function (res) {
          resolve(res);
        },
        fail: function (e) {
          reject({ message: e.errMsg, type: 1 });
        },
        cancel: function (res) {
          reject({ message: '用户拒绝授权获取地理位置', type: 2 });
        }
      });
    }
  })
}

// 统一关闭窗口
export function closeWindow() {
  if (isDingTalk()) {
    dd.biz.navigation.close();
  } else {
    wx.closeWindow(); // 微信和企微都支持这个
  }
}

// ================= 签名与辅助工具 =================

// 获取页面地址 (处理 hash 路由)
function getSignatureUrl() {
  // 企业微信签名验证非常严格，需要确认是 hash 前还是 hash 后，通常是 location.href.split('#')[0]
  // 但如果是 hash 模式路由，部分 Android 机型需要完整的 url
  const index = location.href.indexOf('#')
  const url = location.href.substring(0, index > -1 ? index - 1 : location.href.length)
  return url
}

// 获取签名 (针对微信/企微)
// 注意：企业微信的签名接口通常与公众号不同，可能需要后端增加一个参数来区分
export async function getSignature() {
  let signature
  // if (sessionStorage['signature']) {
  //   signature = JSON.parse(sessionStorage['signature'])
  // }

  if (!signature) {
    const nonceStr = randomString(16)
    const url = getSignatureUrl()
    
    // 这里调用后端接口。注意：企业微信的 JSAPI Ticket 获取方式与公众号不同
    // 你可能需要给 getJsapiTicket 传参区分平台，例如 { url: url, type: isWeCom() ? 'wecom' : 'mp' }
    const { data: jsapi_ticket } = await getJsapiTicket({ url: url, isWeCom: isWeCom() })
    
    const timestamp = ~~(new Date().getTime() / 1000)

    const encodeStr = `jsapi_ticket=${jsapi_ticket}&noncestr=${nonceStr}&timestamp=${timestamp}&url=${url}`
    const str = sha1(encodeStr)

    signature = {
      url,
      // 如果是企微，这里要是 CorpID
      appId: isWeCom() ? WECOM_CORPID : WX_APPID,
      timestamp,
      nonceStr,
      signature: str,
      jsapi_ticket,
    }

    sessionStorage['signature'] = JSON.stringify(signature)
  }

  return signature
}

export function randomString(len) {
  len = len || 32;
  var $chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678';
  var maxPos = $chars.length;
  var pwd = '';
  for (let i = 0; i < len; i++) {
    pwd += $chars.charAt(Math.floor(Math.random() * maxPos));
  }
  return pwd;
}